import createBrowserHistory from 'history/createBrowserHistory';
import { path, contains } from 'ramda';

import {
  GUEST,
  FACEBOOK,
  GOOGLE,
  B2W,
  B2W_RECV,
  TELEMARKETER,
  removeFacebookAvatar,
  setTelemarketerCookies,
  setPrivacyPolicyAgreement
} from '../api/customer/login';
import {
  dispatchEvents,
  persisteCustomerAndDispatchEvents
} from '../api/customer/persiste-customer';
import {
  b2wAuthentication,
  facebookAuthentication,
  googleAuthentication,
  guestAuthentication,
  telemarketerAuthentication,
  telemarketerCustomerAuthentication,
  telemarketerBuyerAuthentication,
  generateAccessToken
} from '../api/account/authenticate';
import { buildNextUrl } from '../commons/utils/buildNextUrl';
import getCustomer from '../api/restql/login-info';

import isPurchaseFlow from '../commons/utils/is-purchase-flow';

import config from '../generated/config';
import { getFinanceSegregation } from '../api/finance/get';
import { createAcceptPrivacyPolicyAgreementRequest } from '../api/customer/privacy-policy-agreement';
import axios from 'axios';

const {
  url: { home: homeUrl, guestRedirect: guestUrl, pagamento: pagamentoUrl }
} = config;

export const getUserSegments = async ({ id }) => {
  try {
    const response = await axios(`https://user-segment-v1-americanas.b2w.io/segment?c_customerId=${id}`);
    const content = path(['data'], response);

    return content;
  } catch (err) {
    console.error(err);
    return;
  }
};

export const getB2BWareHouse = async ({id, token}) => {
  try {
    return await axios({
      url: `https://b2b-warehouse-matcher.b2w.io/warehouse`,
      headers: {
        'X-Customer-Id': id,
        'X-Customer-Token': token
      },
      timeout: 2500,
    });
  } catch (error) {
    console.error(error.response);
    return error.response;
  }
}

export const getCustomerType = async (userSegments) => {
  try {
    const vibraSegment = userSegments.segments.indexOf('913') >= 0;
    return  vibraSegment ? 'vibra' : '';
  } catch (error) {
    console.error(error);
    return;
  }
} 

export async function authenticate(authenticationInfo) {
  try {
    removeFacebookAvatar();
    const type = getAuthenticationType(authenticationInfo);
    const token = await doAuthenticate(authenticationInfo);
    const customerPromise = getCustomer(token);
    const finance = getFinanceSegregation(token);
    const userSegments = await getUserSegments(token);
    const customerType = await getCustomerType(userSegments);

    const customer = await customerPromise;
    if (!customer.privacyPolicyV1) {
      await acceptPrivacyPolicy(token);
    } else {
      setPrivacyPolicyAgreement(1);
    }

    persisteCustomerAndDispatchEvents({
      token,
      customer,
      type,
      finance: await finance,
      userSegments,
      customerType,
    });

    return { token, customer: await customerPromise, type };
  } catch (error) {
    return Promise.reject(error);
  }
}

export default authenticate;

async function acceptPrivacyPolicy({ token, id }) {
  try {
    await axios(
      createAcceptPrivacyPolicyAgreementRequest({
        customerId: id,
        customerToken: token
      })
    );
    setPrivacyPolicyAgreement(1);
    return;
  } catch (error) {
    console.error('Não foi possível aceitar as politicas de privacidade para o usuário.');
  }
}

export async function login2StepAuthenticate({token, type}) {
// export async function login2Step(authenticationInfo) {
  try {
    removeFacebookAvatar();
    const customerPromise = getCustomer(token);
    const finance = getFinanceSegregation(token);
    const userSegments = await getUserSegments(token);
    const warehouse = await getB2BWareHouse(token);
    const customerType = await getCustomerType(userSegments);

    const customer = await customerPromise;
    if (!customer.privacyPolicyV1) {
      await acceptPrivacyPolicy(token);
    } else {
      setPrivacyPolicyAgreement(1);
    }

    persisteCustomerAndDispatchEvents({
      token,
      customer,
      type,
      warehouse,
      finance: await finance,
      userSegments,
      customerType,
    });

    const data = { token, customer: await customerPromise, type };

    return data;
  } catch (error) {
    return Promise.reject(error);
  }
}

export async function login2Step(authenticationInfo) {
  try {
    removeFacebookAvatar();
    const type = getAuthenticationType(authenticationInfo);
    const token = await doAuthenticate(authenticationInfo);

    return { token, type };
  } catch (error) {
    return Promise.reject(error);
  }
}

export async function loginTelemarketer(authenticationInfo) {
  try {
    const tokenResult = await telemarketerAuthentication(authenticationInfo);
    const { id, token } = tokenResult;

    setTelemarketerCookies({ id, token });
    dispatchEvents(tokenResult, {}, TELEMARKETER);

    return { id, token };
  } catch (error) {
    return Promise.reject(error);
  }
}

export async function loginCustomerWithTelemarketer(authenticationInfo) {
  try {
    const tokenResult = await telemarketerCustomerAuthentication(authenticationInfo);
    const { id, token } = tokenResult;
    const customer = await getCustomer({ id, token });

    return { tokenResult, customer };
  } catch (error) {
    return Promise.reject(error);
  }
}

export async function loginBuyerWithTelemarketer(authenticationInfo) {
  try {
    const tokenResult = await telemarketerBuyerAuthentication(authenticationInfo);
    const { id, token } = tokenResult;
    const customer = await getCustomer({ id, token });

    return { tokenResult, customer };
  } catch (error) {
    return Promise.reject(error);
  }
}

const oldCustomerPurchaseContraints = [
  'WITHOUT_MAIN_PHONE',
  'DOCUMENT_NUMER_REQUIRED',
  'BIRTHDAY_REQUIRED'
]; //Remover quando o customer migrar para v6

const newCustomerPurchaseContraints = [
  'INVALID_NAME_LENGTH',
  'INVALID_DOCUMENT_NUMBER',
  'REQUIRED_DOCUMENT_NUMBER',
  'REQUIRED_MAIN_PHONE',
  'REQUIRED_BIRTHDAY'
];

const customerPurchaseContraints = [
  ...oldCustomerPurchaseContraints,
  ...newCustomerPurchaseContraints
];

function customerHasContraints(customer) {
  const constraints = customer.constraints || [];

  return (
    constraints.filter(constraint => contains(constraint.code, customerPurchaseContraints)).length >
    0
  );
}

async function doAuthenticate(authenticationInfo) {
  switch (getAuthenticationType(authenticationInfo)) {
    case GUEST:
      return await guestAuthentication(authenticationInfo);
    case FACEBOOK:
      return await facebookAuthentication(authenticationInfo);
    case GOOGLE:
      return await googleAuthentication(authenticationInfo);
    case B2W:
      return await b2wAuthentication(authenticationInfo);
    case B2W_RECV:
      return await generateAccessToken(authenticationInfo);
    default:
      break;
  }
}

function getAuthenticationType(authenticationInfo) {
  if (authenticationInfo.cpf && authenticationInfo.orderId) {
    return GUEST;
  } else if (path(['authResponse', 'accessToken'], authenticationInfo)) {
    return FACEBOOK;
  } else if (path(['id_token'], authenticationInfo)) {
    return GOOGLE;
  } else if (authenticationInfo.email && authenticationInfo.password) {
    return B2W;
  }
  if (authenticationInfo.customerId && authenticationInfo.code) {
    return B2W_RECV;
  }
}

export function getStepAfterLogin(authentication) {
  if (authentication.type === GUEST) {
    return { url: guestUrl };
  }

  const { location } = createBrowserHistory();

  if (isPurchaseFlow()) {
    if (
      customerHasContraints(authentication.customer) &&
      !authentication.customer.isCorporateCustomer
    ) {
      return { url: `/completar-cadastro${location.search}`, push: true };
    }

    return { url: pagamentoUrl };
  }
  return { url: buildNextUrl(location.search, homeUrl) };
}

export const getErrorMessageByErrorCode = (errorCode, defaultErrorMessage) => {
  switch (errorCode) {
    case 'unknown':
      return 'Ops! Algo não saiu como o esperado, tente novamente mais tarde';

    default:
      return defaultErrorMessage;
  }
};

export const isCustomerLocked = errorCode => errorCode === 'LOCKED';
